import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Navigate } from 'react-router-dom';
import { AppBackButton } from '../components/AppBackButton';
import { 
  Plus, Play, Pause, Trash2, ArrowLeft, BookOpen, Volume2, Sparkles, AlertCircle, FileText, Check, Loader2, Music, Eye, Upload, Headphones, Search, RefreshCw
} from 'lucide-react';
import { supabase } from '../services/supabaseClient';
import { betaEpubStorage, SavedEpubFile } from '../services/betaEpubStorage';
import { parseEpubFile, ParsedEpub, SmilClip, parseSmilClock } from '../services/betaEpubParser';
import { syncAudiobookStorage, SentenceItem, SavedSyncAudiobook, SavedSyncAudiobookMeta, SyncedJsonData } from '../services/syncAudiobookStorage';
import { useSyncedAudiobook } from '../contexts/SyncedAudiobookContext';
import { TestAlignSection } from '../components/TestAlignSection';

import { 
  isPrerecordedAudioEnabled, 
  speakSingleWordPrerecorded, 
  stopPrerecordedAudio, 
  loadR2JsonMapping, 
  normalizeR2Lang,
  lookupR2WordItem,
  getR2AudioUrl,
  webAudioR2Player,
  playR2Audio
} from '../services/prerecordedTtsService';
import { textToSpeechService } from '../services/textToSpeechService';
import { sanitizeHtml } from '../utils/sanitizeHtml';

const R2_BASE_URL = 'https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev';

interface R2CatalogItem {
  id: string;
  status: string;
  title: string;
  author: string;
  language: string;
  accuracy_percentage?: number;
  total_sentences?: number;
  duration_seconds?: number;
}

const formatDuration = (seconds?: number) => {
  if (!seconds || isNaN(seconds) || seconds <= 0) return null;
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  if (hours > 0) {
    return `${hours}h ${minutes > 0 ? `${minutes}m` : ''}`;
  }
  return `${minutes}m`;
};

interface R2BookCardProps {
  item: R2CatalogItem;
  isLoading: boolean;
  onOpen: (item: R2CatalogItem) => void;
}

const R2BookCard: React.FC<R2BookCardProps> = ({ item, isLoading, onOpen }) => {
  const [imgError, setImgError] = useState(false);
  const coverUrl = `https://www.gutenberg.org/cache/epub/${item.id}/pg${item.id}.cover.medium.jpg`;
  const formattedDuration = formatDuration(item.duration_seconds);

  return (
    <div
      onClick={() => !isLoading && onOpen(item)}
      className="group relative bg-slate-900/90 hover:bg-slate-850 border border-slate-800 hover:border-warn/50 rounded-3xl p-4 transition-all duration-300 cursor-pointer flex flex-col justify-between overflow-hidden hover:"
    >
      {/* Cover Image Container */}
      <div className="relative w-full aspect-[2/3] rounded-2xl overflow-hidden bg-slate-950 mb-3 border border-white/10 group-hover:scale-[1.02] transition-transform duration-300">
        {!imgError ? (
          <img
            src={coverUrl}
            alt={item.title}
            onError={() => setImgError(true)}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-tr from-tint-butter via-tint-lavender to-slate-900 flex flex-col items-center justify-center p-4 text-center">
            <span className="text-4xl mb-2">📚</span>
            <h4 className="font-normal text-xs text-surface line-clamp-3 uppercase tracking-[0.14em] font-mono">{item.title}</h4>
            <p className="text-[10px] text-slate-300 line-clamp-2 mt-1">{item.author}</p>
          </div>
        )}

        {/* Hover Play Overlay */}
        <div className="absolute inset-0 bg-slate-950/60 backdrop-blur-xs opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          {isLoading ? (
            <Loader2 className="w-10 h-10 text-warn animate-spin" />
          ) : (
            <div className="w-12 h-12 rounded-full bg-warn text-ink flex items-center justify-center scale-90 group-hover:scale-100 transition-transform">
              <Play size={24} className="ml-1" fill="currentColor" />
            </div>
          )}
        </div>

        {/* Language Badge */}
        <div className="absolute top-2 left-2 px-2.5 py-1 bg-slate-950/80 backdrop-blur-md text-warn font-mono text-[10px] font-medium rounded-lg border border-warn/30">
          {item.language}
        </div>
      </div>

      {/* Book Metadata - Strictly Title, Author, Language & Duration ONLY */}
      <div className="space-y-1">
        <h4 className="font-semibold text-sm text-white group-hover:text-warn transition-colors line-clamp-1">
          {item.title}
        </h4>
        <p className="text-xs text-slate-400 font-medium line-clamp-1">{item.author}</p>
        
        {formattedDuration && (
          <div className="pt-1.5 flex items-center gap-1.5 text-slate-400 text-xs font-semibold">
            <Headphones size={13} className="text-warn/80" />
            <span>{formattedDuration}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default function BetaSyncPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { playAudiobook, activeAudiobook, closeAudiobook } = useSyncedAudiobook();

  // Security & Access state
  const [authStatus, setAuthStatus] = useState<'checking' | 'allowed' | 'denied'>('checking');

  // Subtab detection
  const isSyncAudiobooksTab = location.pathname.includes('sync-audiobooks');
  const isTestsAlignTab = location.pathname.includes('tests-align');

  // R2 Catalog State
  const [r2Catalog, setR2Catalog] = useState<R2CatalogItem[]>([]);
  const [isLoadingR2Catalog, setIsLoadingR2Catalog] = useState(false);
  const [r2CatalogError, setR2CatalogError] = useState<string | null>(null);
  const [loadingR2BookId, setLoadingR2BookId] = useState<string | null>(null);
  const [catalogSearch, setCatalogSearch] = useState('');
  const [selectedLanguageFilter, setSelectedLanguageFilter] = useState<string>('all');

  // Sync Audiobooks State
  const [syncBooks, setSyncBooks] = useState<SavedSyncAudiobookMeta[]>([]);
  const [jsonFile, setJsonFile] = useState<File | null>(null);
  const [mp3File, setMp3File] = useState<File | null>(null);
  const [isImportingSync, setIsImportingSync] = useState(false);
  const [syncImportError, setSyncImportError] = useState<string | null>(null);

  // EPUB List state
  const [savedEpubs, setSavedEpubs] = useState<Omit<SavedEpubFile, 'fileData'>[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  // Active Reader state
  const [activeEpubId, setActiveEpubId] = useState<string | null>(null);
  const [parsedEpub, setParsedEpub] = useState<ParsedEpub | null>(null);
  const [currentChapterIdx, setCurrentChapterIdx] = useState(0);
  const [isLoadingReader, setIsLoadingReader] = useState(false);

  // Playback & Sync state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentClip, setCurrentClip] = useState<SmilClip | null>(null);
  const [highlightEnabled, setHighlightEnabled] = useState(true);
  const [audioError, setAudioError] = useState<string | null>(null);
  const [activeAudioSrc, setActiveAudioSrc] = useState<string | null>(null);

  const audioRef = useRef<HTMLAudioElement | null>(null);
  const readerContainerRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const jsonInputRef = useRef<HTMLInputElement | null>(null);
  const mp3InputRef = useRef<HTMLInputElement | null>(null);

  // Beta R2 TTS Tool State
  const [ttsInputText, setTtsInputText] = useState('Mon équipe et moi cherchons une solution.');
  const [selectedLang, setSelectedLang] = useState('fr');
  const [isPlayingContinuous, setIsPlayingContinuous] = useState(false);
  const [isPrerecordedEnabled, setIsPrerecordedEnabled] = useState(true);

  const [executionReport, setExecutionReport] = useState<any>(null);
  const stopContinuousRef = useRef(false);

  useEffect(() => {
    const handleStorage = () => setIsPrerecordedEnabled(true);
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // 1. Silent Auth check strictly for willianrgracec@gmail.com
  useEffect(() => {
    let isMounted = true;
    async function verifyServerAccess() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session || !session.user || session.user.email !== 'willianrgracec@gmail.com') {
          if (isMounted) setAuthStatus('denied');
          return;
        }

        if (isMounted) setAuthStatus('allowed');
      } catch {
        if (isMounted) setAuthStatus('denied');
      }
    }
    verifyServerAccess();
    return () => { isMounted = false; };
  }, []);

  // 2. Load stored items when authorized
  useEffect(() => {
    if (authStatus === 'allowed') {
      loadSavedEpubs();
      loadSyncAudiobooks();
    }
  }, [authStatus]);

  // 3. Load R2 Catalog when authorized and subtab is sync-audiobooks
  useEffect(() => {
    if (authStatus === 'allowed' && isSyncAudiobooksTab) {
      fetchR2Catalog();
    }
  }, [authStatus, isSyncAudiobooksTab]);

  const fetchR2Catalog = async () => {
    setIsLoadingR2Catalog(true);
    setR2CatalogError(null);
    try {
      let catalogItems: any[] = [];
      try {
        const res = await fetch(`/api/sync-audiobooks/catalog?limit=5000`);
        if (res.ok) {
          const data = await res.json();
          if (data && Array.isArray(data.items)) {
            catalogItems = data.items;
          }
        }
      } catch (e) {}

      if (catalogItems.length === 0) {
        try {
          const localRes = await fetch('/audiobooks-final-catalog.json');
          if (localRes.ok) catalogItems = await localRes.json();
        } catch (e) {}
      }

      if (catalogItems.length === 0) {
        try {
          const cdnRes = await fetch('https://pub-3d7ad7f5d04d46a5b7232c0baf46d866.r2.dev/audiobooks-final/catalog.json');
          if (cdnRes.ok) catalogItems = await cdnRes.json();
        } catch (e) {}
      }

      if (catalogItems.length === 0) {
        throw new Error('Falha ao carregar o catálogo de audiobooks sincronizados.');
      }

      setR2Catalog(catalogItems);
    } catch (err: any) {
      setR2CatalogError(err.message || 'Erro ao carregar catálogo.');
    } finally {
      setIsLoadingR2Catalog(false);
    }
  };

  const handleOpenR2Audiobook = async (item: any) => {
    setLoadingR2BookId(item.id);
    try {
      const infoUrl = `/api/sync-audiobooks/${encodeURIComponent(item.id)}/info?folder=${encodeURIComponent(item.folder || '')}`;
      const res = await fetch(infoUrl);
      if (!res.ok) throw new Error(`Falha ao carregar arquivo de sincronia para "${item.title}".`);
      const info = await res.json();

      const audiobookObj: SavedSyncAudiobook = {
        id: `r2_${item.id}`,
        book_id: item.id,
        slug: info.slug || item.slug || item.id,
        folder: info.folder || item.folder,
        title: info.title || item.title,
        author: info.author || item.author,
        language: info.language || item.language,
        total_duration_seconds: info.total_duration_seconds || item.duration_seconds,
        sentences: info.sentences || [],
        audioSrc: info.audioUrl || `/api/audiobooks/${encodeURIComponent(info.id)}/stream?folder=${encodeURIComponent(info.folder || item.folder || '')}&slug=${encodeURIComponent(info.slug || item.slug || '')}`,
        addedAt: Date.now(),
        savedTime: 0,
        syncOffset: 0
      };

      playAudiobook(audiobookObj);
    } catch (err: any) {
      alert(err.message || 'Erro ao abrir audiobook sincronizado.');
    } finally {
      setLoadingR2BookId(null);
    }
  };

  const loadSyncAudiobooks = async () => {
    const list = await syncAudiobookStorage.getAllAudiobooks();
    setSyncBooks(list);
  };

  const loadSavedEpubs = async () => {
    const epubs = await betaEpubStorage.getAllEpubs();
    setSavedEpubs(epubs);
  };

  // Import JSON + MP3 + Optional Text Handler
  const handleImportSyncFiles = async () => {
    if (!jsonFile || !mp3File) {
      setSyncImportError('Por favor, selecione os arquivos obrigatórios: JSON de Sincronia e Áudio MP3.');
      return;
    }

    setIsImportingSync(true);
    setSyncImportError(null);

    try {
      const jsonText = await jsonFile.text();
      let jsonData: any = null;
      try {
        jsonData = JSON.parse(jsonText);
      } catch (pErr: any) {
        throw new Error('Formato de JSON inválido. Verifique se o arquivo possui sintaxe JSON válida.');
      }

      // Extract array of sentence items robustly (supports raw array or objects with sentences/clips/items/data/chunks/segments)
      const rawSentences: any[] = Array.isArray(jsonData)
        ? jsonData
        : (jsonData.sentences || jsonData.clips || jsonData.items || jsonData.data || jsonData.chunks || jsonData.segments || []);

      if (!Array.isArray(rawSentences) || rawSentences.length === 0) {
        throw new Error('Arquivo JSON inválido: nenhuma frase ou marcação de tempo foi encontrada no arquivo.');
      }

      const sentences: SentenceItem[] = rawSentences.map((item: any, idx: number) => ({
        id: String(item.id || `sentence_${idx}`),
        tag: String(item.tag || `p_${idx + 1}`),
        text: String(item.text || item.sentence || item.content || '').trim(),
        html: item.html ? String(item.html) : undefined,
        start: typeof item.start === 'number' ? item.start : (parseFloat(item.start) || 0),
        end: typeof item.end === 'number' ? item.end : (parseFloat(item.end) || 0),
        duration: typeof item.duration === 'number' ? item.duration : (parseFloat(item.duration) || (item.end && item.start ? item.end - item.start : 0))
      }));

      const title = (jsonData && !Array.isArray(jsonData) && jsonData.title) || jsonFile.name.replace(/\.json$/i, '').replace(/_/g, ' ') || 'Audiobook Sincronizado';
      const author = (jsonData && !Array.isArray(jsonData) && jsonData.author) || 'Autor Desconhecido';
      const language = (jsonData && !Array.isArray(jsonData) && jsonData.language) || 'en';

      const maxEnd = sentences.reduce((max, s) => Math.max(max, s.end), 0);
      const totalDuration = (jsonData && !Array.isArray(jsonData) && jsonData.total_duration_seconds) || maxEnd;

      const formattedJsonData: SyncedJsonData = {
        book_id: (jsonData && !Array.isArray(jsonData) && jsonData.book_id) || `imported_${Date.now()}`,
        title,
        author,
        language,
        total_duration_seconds: totalDuration,
        total_sentences: sentences.length,
        sentences
      } as any;

      const saved = await syncAudiobookStorage.saveAudiobook(formattedJsonData, mp3File);
      await loadSyncAudiobooks();

      // Create Object URL for clean, memory-safe, byte-range streaming of large MP3 file (~700MB)
      const audioObjectUrl = URL.createObjectURL(mp3File);
      const fullBook: SavedSyncAudiobook = {
        ...saved,
        sentences: sentences.slice(0, 20),
        audioBlob: mp3File,
        audioSrc: audioObjectUrl,
      };

      setJsonFile(null);
      setMp3File(null);

      playAudiobook(fullBook);
    } catch (err: any) {
      console.error('[BetaSyncPage] Import error:', err);
      setSyncImportError(err.message || 'Falha ao processar os arquivos de sincronia.');
    } finally {
      setIsImportingSync(false);
    }
  };

  const handleDeleteSyncBook = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Deseja realmente remover este audiobook sincronizado?')) {
      await syncAudiobookStorage.deleteAudiobook(id);
      if (activeAudiobook?.id === id) {
        closeAudiobook();
      }
      await loadSyncAudiobooks();
    }
  };

  const handleOpenSyncBook = async (id: string) => {
    const full = await syncAudiobookStorage.getAudiobook(id);
    if (full) {
      playAudiobook({
        ...full,
        sentences: (full.sentences || []).slice(0, 20)
      });
    }
  };

  // Handle EPUB File Upload
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    if (!file.name.toLowerCase().endsWith('.epub')) {
      setUploadError('Por favor, selecione um arquivo válido no formato .epub');
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      const buffer = await file.arrayBuffer();
      const title = file.name.replace(/\.epub$/i, '');
      const saved = await betaEpubStorage.saveEpub(file.name, title, buffer);
      await loadSavedEpubs();
      await openEpubReader(saved.id);
    } catch (err: any) {
      setUploadError(err.message || 'Erro ao importar o arquivo EPUB.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Delete EPUB
  const handleDeleteEpub = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm('Deseja realmente remover este arquivo de teste?')) {
      await betaEpubStorage.deleteEpub(id);
      if (activeEpubId === id) {
        closeReader();
      }
      await loadSavedEpubs();
    }
  };

  // Open EPUB Reader
  const openEpubReader = async (id: string) => {
    setIsLoadingReader(true);
    setActiveEpubId(id);
    setAudioError(null);
    setCurrentChapterIdx(0);
    setIsPlaying(false);

    try {
      const record = await betaEpubStorage.getEpub(id);
      if (!record) {
        throw new Error('Arquivo não encontrado no armazenamento local.');
      }

      const parsed = await parseEpubFile(record.fileData);
      setParsedEpub(parsed);
    } catch (err: any) {
      setAudioError(`Falha ao ler pacote EPUB: ${err.message}`);
    } finally {
      setIsLoadingReader(false);
    }
  };

  const closeReader = () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    setActiveEpubId(null);
    setParsedEpub(null);
    setIsPlaying(false);
    setCurrentClip(null);
    setActiveAudioSrc(null);
  };

  const currentChapter = parsedEpub?.chapters[currentChapterIdx] || null;

  const playClip = (clip: SmilClip) => {
    if (!parsedEpub) return;
    
    let audioUrl = parsedEpub.audioBlobs.get(clip.audioSrc);
    if (!audioUrl) {
      const filename = clip.audioSrc.split('/').pop();
      if (filename) audioUrl = parsedEpub.audioBlobs.get(filename);
    }

    if (!audioUrl) {
      setAudioError(`Áudio não encontrado para: ${clip.audioSrc}`);
      setIsPlaying(false);
      return;
    }

    setAudioError(null);
    setCurrentClip(clip);

    if (audioRef.current) {
      if (activeAudioSrc !== audioUrl) {
        audioRef.current.src = audioUrl;
        setActiveAudioSrc(audioUrl);
      }
      audioRef.current.currentTime = clip.clipBegin;
      audioRef.current.play().then(() => {
        setIsPlaying(true);
      }).catch(() => {});
    }
  };

  const togglePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      if (currentClip) {
        audioRef.current.play();
        setIsPlaying(true);
      } else if (currentChapter && currentChapter.smilClips.length > 0) {
        playClip(currentChapter.smilClips[0]);
      }
    }
  };

  const handleStopContinuousPlay = () => {
    stopContinuousRef.current = true;
    setIsPlayingContinuous(false);
    stopPrerecordedAudio();
    textToSpeechService.stop();
  };

  const handleStartContinuousPlay = async () => {
    if (!ttsInputText.trim()) return;
    
    stopContinuousRef.current = false;
    setIsPlayingContinuous(true);
    setExecutionReport(null);

    const matches = ttsInputText.match(/[\p{L}\p{N}]+[^\s\p{L}\p{N}]*|[^\s\p{L}\p{N}]+/gu) || [];
    const tokens = matches.map(m => {
      const wordMatch = m.match(/[\p{L}\p{N}]+/u);
      const word = wordMatch ? wordMatch[0] : '';
      const punct = m.replace(/[\p{L}\p{N}]+/gu, '');
      let pauseMs = 0;
      if (/[.!?]/.test(punct)) pauseMs = 550;
      else if (/[,;:]/.test(punct)) pauseMs = 280;
      return { token: m, word, punct, pauseMs };
    }).filter(t => t.word);

    if (tokens.length === 0) {
      setIsPlayingContinuous(false);
      return;
    }

    const reportData = {
      totalWords: tokens.length,
      r2Count: 0,
      fallbackCount: 0,
      missingCount: 0,
      r2Words: [] as string[],
      fallbackWords: [] as string[],
      missingWords: [] as string[],
      details: [] as Array<{ token: string; word: string; method: string; url?: string }>
    };

    const wordResolutions = await Promise.all(tokens.map(async t => {
      const lookup = await lookupR2WordItem(t.word, selectedLang);
      if (lookup) {
        const url = getR2AudioUrl(lookup.item, lookup.config);
        const buffer = await webAudioR2Player.loadBuffer(url);
        return { lookup, url, buffer };
      }
      return null;
    }));

    for (let i = 0; i < tokens.length; i++) {
      if (stopContinuousRef.current) break;

      const item = tokens[i];
      const resData = wordResolutions[i];

      let playedViaR2 = false;
      let playedUrl: string | undefined = undefined;

      if (resData && resData.buffer) {
        playedUrl = resData.url;
        playedViaR2 = await webAudioR2Player.playBuffer(resData.buffer);
      } else if (resData && resData.url) {
        playedUrl = resData.url;
        playedViaR2 = await playR2Audio(resData.url);
      }

      if (playedViaR2) {
        reportData.r2Count++;
        reportData.r2Words.push(item.word);
        reportData.details.push({
          token: item.token,
          word: item.word,
          method: 'R2 Audio (Nativo R2)',
          url: playedUrl
        });
      } else {
        reportData.fallbackCount++;
        reportData.fallbackWords.push(item.word);
        if (!resData) {
          reportData.missingCount++;
          reportData.missingWords.push(item.word);
        }
        reportData.details.push({
          token: item.token,
          word: item.word,
          method: 'TTS Nativo (Fallback Dispositivo)'
        });
        await textToSpeechService.speakNativeDeviceTts(item.word, selectedLang);
      }

      if (item.pauseMs > 0 && !stopContinuousRef.current) {
        await new Promise(r => setTimeout(r, item.pauseMs));
      }
    }

    setIsPlayingContinuous(false);
    setExecutionReport({
      status: stopContinuousRef.current ? 'Interrompido pelo usuário' : 'Concluído com Sucesso',
      total: reportData.totalWords,
      r2Count: reportData.r2Count,
      fallbackCount: reportData.fallbackCount,
      missingCount: reportData.missingCount,
      r2Words: reportData.r2Words,
      fallbackWords: reportData.fallbackWords,
      missingWords: reportData.missingWords,
      details: reportData.details
    });
  };

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      const curTime = audio.currentTime;
      if (!currentChapter || currentChapter.smilClips.length === 0) return;
      const active = currentChapter.smilClips.find(c => curTime >= c.clipBegin && curTime <= c.clipEnd);
      if (active && active !== currentClip) {
        setCurrentClip(active);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentClip(null);
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [currentChapter, currentClip]);

  useEffect(() => {
    if (!readerContainerRef.current) return;
    const prevHighlighted = readerContainerRef.current.querySelectorAll('.beta-narrated-active');
    prevHighlighted.forEach(el => {
      el.classList.remove('beta-narrated-active', 'bg-tint-butter', 'dark:bg-warn/40', 'font-bold', 'border-l-4', 'border-warn', 'px-2', 'py-1', 'rounded', 'transition-all');
    });

    if (!highlightEnabled || !currentClip) return;
    const targetEl = readerContainerRef.current.querySelector(`#${currentClip.elementId}`) ||
                     readerContainerRef.current.querySelector(`[data-id="${currentClip.elementId}"]`);

    if (targetEl) {
      targetEl.classList.add('beta-narrated-active', 'bg-tint-butter', 'dark:bg-warn/40', 'font-bold', 'border-l-4', 'border-warn', 'px-2', 'py-1', 'rounded', 'transition-all');
      targetEl.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [currentClip, highlightEnabled, currentChapterIdx]);

  const handleReaderClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!currentChapter || currentChapter.smilClips.length === 0) return;
    let target: HTMLElement | null = e.target as HTMLElement;
    while (target && target !== readerContainerRef.current) {
      if (target.id) {
        const foundClip = currentChapter.smilClips.find(c => c.elementId === target!.id || c.textSrc.endsWith(`#${target!.id}`));
        if (foundClip) {
          playClip(foundClip);
          return;
        }
      }
      target = target.parentElement;
    }
  };

  // If denied access -> Redirect quietly WITHOUT any console logs or leaks
  if (authStatus === 'denied') {
    return <Navigate to="/" replace />;
  }

  if (authStatus === 'checking') {
    return (
      <div className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-6 text-white">
        <Loader2 className="w-10 h-10 text-warn animate-spin mb-4" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 p-4 md:p-8 space-y-6 max-w-6xl mx-auto pb-24">
      {/* Hidden Audio Element for EPUB parser */}
      <audio ref={audioRef} />

      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-800 pb-6">
        <div className="flex items-center gap-3">
          {activeEpubId
            ? <AppBackButton onClick={closeReader} />
            : <AppBackButton fallbackTo="/settings" />}
          <div>
            <h1 className="text-xl md:text-2xl font-medium flex items-center gap-2 text-white">
              <Sparkles className="text-warn" size={24} /> Painel Beta Restrito
            </h1>
            <p className="text-xs text-slate-400 font-medium mt-1">
              Testes de TTS R2, sincronização de EPUB3 e novos Audiobooks Sincronizados.
            </p>
          </div>
        </div>

        {/* Sub-tabs Navigation */}
        {!activeEpubId && (
          <div className="flex items-center gap-2 bg-slate-900 p-1.5 rounded-2xl border border-slate-800">
            <button
              onClick={() => navigate('/settings/beta')}
              className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all ${
                !isSyncAudiobooksTab && !isTestsAlignTab
                  ? 'bg-warn text-ink'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              Ferramentas Beta / TTS
            </button>
            <button
              onClick={() => navigate('/settings/beta/sync-audiobooks')}
              className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-2 ${
                isSyncAudiobooksTab
                  ? 'bg-warn text-ink'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Headphones size={16} /> Audiobooks Sincronizados
            </button>
            <button
              onClick={() => navigate('/settings/beta/tests-align')}
              className={`px-4 py-2 rounded-xl text-xs font-semibold transition-all flex items-center gap-2 ${
                isTestsAlignTab
                  ? 'bg-warn text-ink'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <Sparkles size={16} /> test align
            </button>
          </div>
        )}

        {!activeEpubId && !isSyncAudiobooksTab && !isTestsAlignTab && (
          <div>
            <input 
              type="file" 
              ref={fileInputRef} 
              onChange={handleFileUpload} 
              accept=".epub" 
              className="hidden" 
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
              className="px-5 py-3 bg-warn hover:bg-warn active:scale-95 text-ink font-medium text-[13px] rounded-2xl flex items-center gap-2 transition-all disabled:opacity-50 cursor-pointer"
            >
              {isUploading ? <Loader2 className="animate-spin" size={18} /> : <Plus size={18} />}
              Importar EPUB
            </button>
          </div>
        )}

        {!activeEpubId && isSyncAudiobooksTab && (
          <div className="flex items-center gap-2 flex-wrap">
            <input 
              type="file" 
              ref={jsonInputRef} 
              onChange={(e) => setJsonFile(e.target.files?.[0] || null)} 
              accept=".json" 
              className="hidden" 
            />
            <input 
              type="file" 
              ref={mp3InputRef} 
              onChange={(e) => setMp3File(e.target.files?.[0] || null)} 
              accept=".mp3,audio/mpeg,audio/mp3" 
              className="hidden" 
            />
            <button
              onClick={() => jsonInputRef.current?.click()}
              className={`px-4 py-2.5 rounded-2xl text-xs font-semibold flex items-center gap-2 border transition-all cursor-pointer ${
                jsonFile 
                  ? 'bg-ok/20 text-ok border-ok/40' 
                  : 'bg-slate-900 hover:bg-slate-800 text-warn border-slate-800 hover:border-warn/50'
              }`}
              title="Selecionar arquivo JSON de Sincronia"
            >
              <FileText size={16} />
              <span className="truncate max-w-[140px]">{jsonFile ? jsonFile.name : 'Importar JSON'}</span>
            </button>

            <button
              onClick={() => mp3InputRef.current?.click()}
              className={`px-4 py-2.5 rounded-2xl text-xs font-semibold flex items-center gap-2 border transition-all cursor-pointer ${
                mp3File 
                  ? 'bg-ok/20 text-ok border-ok/40' 
                  : 'bg-slate-900 hover:bg-slate-800 text-tintd-lavender border-slate-800 hover:border-tintd-lavender/50'
              }`}
              title="Selecionar arquivo de Áudio MP3"
            >
              <Music size={16} />
              <span className="truncate max-w-[140px]">{mp3File ? mp3File.name : 'Importar MP3'}</span>
            </button>

            {jsonFile && mp3File && (
              <button
                onClick={handleImportSyncFiles}
                disabled={isImportingSync}
                className="px-5 py-2.5 bg-warn hover:bg-warn text-ink font-medium text-[13px] rounded-2xl flex items-center gap-2 transition-all cursor-pointer animate-in fade-in active:scale-95"
              >
                {isImportingSync ? <Loader2 size={16} className="animate-spin" /> : <Play size={16} fill="currentColor" />}
                <span>SINCRONIZAR</span>
              </button>
            )}
          </div>
        )}
      </div>

      {/* RENDER SUB-TAB 2: AUDIOBOOKS SINCRONIZADOS */}
      {isSyncAudiobooksTab ? (
        <div className="space-y-10 animate-in fade-in duration-300">
          
          {/* SECTION 1: CLOUDFLARE R2 SYNC AUDIOBOOKS CATALOG */}
          <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-slate-800 pb-4">
              <div>
                <h2 className="text-xl font-semibold text-white flex items-center gap-2">
                  <Headphones className="text-warn" size={24} /> Catálogo de Audiobooks Sincronizados
                </h2>
                <p className="text-xs text-slate-400 font-medium mt-1">
                  Biblioteca estática hospedada no Cloudflare R2 ({r2Catalog.length} títulos disponíveis)
                </p>
              </div>

              {/* Search & Refresh Controls */}
              <div className="flex items-center gap-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                  <input
                    type="text"
                    value={catalogSearch}
                    onChange={(e) => setCatalogSearch(e.target.value)}
                    placeholder="Buscar por título ou autor..."
                    className="bg-slate-900 border border-slate-800 rounded-2xl pl-9 pr-4 py-2.5 text-xs text-white placeholder:text-slate-500 font-medium outline-none focus:border-warn transition-colors w-64"
                  />
                </div>

                <button
                  onClick={fetchR2Catalog}
                  disabled={isLoadingR2Catalog}
                  className="p-2.5 bg-slate-900 hover:bg-slate-800 text-slate-300 rounded-2xl border border-slate-800 transition-colors"
                  title="Atualizar Catálogo"
                >
                  <RefreshCw size={16} className={isLoadingR2Catalog ? 'animate-spin text-warn' : ''} />
                </button>
              </div>
            </div>

            {/* Language Filter Pills */}
            {r2Catalog.length > 0 && (
              <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-none">
                <button
                  onClick={() => setSelectedLanguageFilter('all')}
                  className={`px-3.5 py-1.5 rounded-xl text-xs font-normal transition-all uppercase font-mono tracking-[0.14em] ${
                    selectedLanguageFilter === 'all'
                      ? 'bg-warn text-ink'
                      : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
                  }`}
                >
                  Todos ({r2Catalog.length})
                </button>
                {Array.from(new Set(r2Catalog.map(b => b.language))).map(lang => {
                  const count = r2Catalog.filter(b => b.language === lang).length;
                  return (
                    <button
                      key={lang}
                      onClick={() => setSelectedLanguageFilter(lang)}
                      className={`px-3.5 py-1.5 rounded-xl text-xs font-normal transition-all uppercase font-mono tracking-[0.14em] ${
                        selectedLanguageFilter === lang
                          ? 'bg-warn text-ink'
                          : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
                      }`}
                    >
                      {lang} ({count})
                    </button>
                  );
                })}
              </div>
            )}

            {/* Catalog Grid State */}
            {isLoadingR2Catalog ? (
              <div className="p-16 text-center bg-slate-900/40 border border-slate-800/80 rounded-3xl space-y-4">
                <Loader2 className="w-10 h-10 text-warn animate-spin mx-auto" />
                <p className="text-xs font-normal uppercase font-mono text-slate-400 tracking-[0.14em]">Carregando catálogo R2...</p>
              </div>
            ) : r2CatalogError ? (
              <div className="p-6 bg-tint-rose/40 border border-tint-rose/60 rounded-3xl text-xs text-bad flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <AlertCircle size={22} className="text-bad shrink-0" />
                  <span>{r2CatalogError}</span>
                </div>
                <button
                  onClick={fetchR2Catalog}
                  className="px-4 py-2 bg-tint-rose hover:bg-tint-rose text-white font-bold rounded-xl transition-colors"
                >
                  Tentar Novamente
                </button>
              </div>
            ) : (
              (() => {
                const filtered = r2Catalog.filter(b => {
                  const matchesSearch = 
                    b.title.toLowerCase().includes(catalogSearch.toLowerCase()) ||
                    b.author.toLowerCase().includes(catalogSearch.toLowerCase());
                  const matchesLang = selectedLanguageFilter === 'all' || b.language === selectedLanguageFilter;
                  return matchesSearch && matchesLang;
                });

                if (filtered.length === 0) {
                  return (
                    <div className="p-12 text-center bg-slate-900/40 border border-slate-800 rounded-3xl space-y-2">
                      <BookOpen size={36} className="text-slate-600 mx-auto" />
                      <p className="text-slate-400 font-bold text-sm">Nenhum audiobook encontrado.</p>
                      <p className="text-slate-600 text-xs">Tente ajustar seus termos de busca ou filtro de idioma.</p>
                    </div>
                  );
                }

                return (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
                    {filtered.map((book) => (
                      <R2BookCard
                        key={book.id}
                        item={book}
                        isLoading={loadingR2BookId === book.id}
                        onOpen={handleOpenR2Audiobook}
                      />
                    ))}
                  </div>
                );
              })()
            )}
          </div>

          {/* SECTION 2: MANUAL IMPORTER & LOCAL LIBRARY */}
          <div className="pt-8 border-t border-slate-800/80 space-y-6">
            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Upload className="text-warn" size={20} /> Novo Importador de Arquivos (JSON + MP3)
                </h2>
                <p className="text-xs text-slate-400 font-medium mt-1 leading-relaxed">
                  Carregue o arquivo <code className="text-warn font-mono">.json</code> de sincronia e o áudio <code className="text-warn font-mono">.mp3</code> correspondente para gerar a leitura sincronizada ultraleve.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Input 1: JSON File */}
                <div className="p-5 bg-slate-950 border border-slate-800 rounded-2xl flex flex-col items-center justify-center text-center space-y-3">
                  <FileText className="text-warn" size={32} />
                  <div>
                    <h4 className="text-xs font-normal text-slate-200 uppercase font-mono tracking-[0.14em]">1. JSON de Sincronia</h4>
                    <p className="text-[11px] text-slate-500 mt-0.5">Mapeamento de sentenças e timestamps</p>
                  </div>
                  <input
                    type="file"
                    accept=".json"
                    onChange={(e) => setJsonFile(e.target.files?.[0] || null)}
                    className="hidden"
                    id="json-file-input"
                  />
                  <label
                    htmlFor="json-file-input"
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-bold cursor-pointer transition-colors border border-slate-700 truncate max-w-full"
                  >
                    {jsonFile ? jsonFile.name : 'Selecionar .json'}
                  </label>
                </div>

                {/* Input 2: MP3 File */}
                <div className="p-5 bg-slate-950 border border-slate-800 rounded-2xl flex flex-col items-center justify-center text-center space-y-3">
                  <Music className="text-tintd-lavender" size={32} />
                  <div>
                    <h4 className="text-xs font-normal text-slate-200 uppercase font-mono tracking-[0.14em]">2. Áudio MP3</h4>
                    <p className="text-[11px] text-slate-500 mt-0.5">Arquivo de áudio completo [id]_FULL.mp3</p>
                  </div>
                  <input
                    type="file"
                    accept=".mp3,audio/mpeg,audio/mp3"
                    onChange={(e) => setMp3File(e.target.files?.[0] || null)}
                    className="hidden"
                    id="mp3-file-input"
                  />
                  <label
                    htmlFor="mp3-file-input"
                    className="px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-xs font-bold cursor-pointer transition-colors border border-slate-700 truncate max-w-full"
                  >
                    {mp3File ? mp3File.name : 'Selecionar .mp3'}
                  </label>
                </div>
              </div>

              {syncImportError && (
                <div className="p-4 bg-tint-rose/40 border border-tint-rose/60 rounded-2xl text-xs text-bad flex items-center gap-3">
                  <AlertCircle size={20} className="shrink-0 text-bad" />
                  <span>{syncImportError}</span>
                </div>
              )}

              <button
                onClick={handleImportSyncFiles}
                disabled={isImportingSync || !jsonFile || !mp3File}
                className={`text-[13px] font-medium w-full h-12 rounded-full flex items-center justify-center gap-2 transition-all ${ jsonFile && mp3File && !isImportingSync ? 'bg-warn hover:bg-warn text-ink cursor-pointer' : 'bg-slate-800 text-slate-500 cursor-not-allowed' }`}
              >
                {isImportingSync ? (
                  <>
                    <Loader2 size={18} className="animate-spin" /> Processando Audiobook...
                  </>
                ) : (
                  <>
                    <Headphones size={18} /> Carregar e Abrir Audiobook Sincronizado
                  </>
                )}
              </button>
            </div>

            {/* Imported Audiobooks Local Grid */}
            {syncBooks.length > 0 && (
              <div className="space-y-4 pt-2">
                <h3 className="text-sm font-medium text-slate-400 flex items-center gap-2">
                  <BookOpen className="text-warn" size={18} /> Audiobooks Sincronizados Importados Localmente ({syncBooks.length})
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {syncBooks.map((book) => (
                    <div
                      key={book.id}
                      onClick={() => handleOpenSyncBook(book.id)}
                      className="p-5 bg-slate-900 hover:bg-slate-850 border border-slate-800 hover:border-warn/40 rounded-3xl transition-all cursor-pointer flex items-center justify-between group"
                    >
                      <div className="flex items-center gap-4 overflow-hidden">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-tr from-warn to-tint-lavender flex items-center justify-center text-white text-xl font-semibold shrink-0 border border-white/10">
                          📚
                        </div>
                        <div className="truncate">
                          <h4 className="font-semibold text-sm text-white group-hover:text-warn transition-colors truncate">
                            {book.title} Sync
                          </h4>
                          <p className="text-xs text-slate-400 font-medium truncate mt-0.5">{book.author}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <span className="px-2 py-0.5 bg-warn/10 text-warn font-mono text-[10px] font-medium rounded-md border border-warn/20">
                              {book.language}
                            </span>
                            {book.total_duration_seconds && formatDuration(book.total_duration_seconds) && (
                              <span className="text-[10px] text-slate-400 font-medium flex items-center gap-1">
                                <Headphones size={11} className="text-warn" />
                                {formatDuration(book.total_duration_seconds)}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <button
                        onClick={(e) => handleDeleteSyncBook(book.id, e)}
                        className="p-3 text-slate-500 hover:text-bad hover:bg-tint-rose/30 rounded-2xl transition-colors shrink-0"
                        title="Excluir Audiobook"
                      >
                        <Trash2 size={20} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      ) : isTestsAlignTab ? (
        <TestAlignSection />
      ) : (
        /* RENDER SUB-TAB 1: ORIGINAL BETA TTS / EPUB TOOLS */
        <>
          {/* Ferramenta Beta TTS R2 (Leitura Contínua) */}
          {!activeEpubId && (
            <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 md:p-8 space-y-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between border-b border-slate-800 pb-4 gap-4">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-2xl bg-warn/10 text-warn">
                    <Volume2 size={24} />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-white">Ferramenta de Teste Beta - TTS R2 (Leitura Contínua)</h2>
                    <p className="text-xs font-semibold text-slate-400 mt-0.5">
                      Valide a leitura encadeada de frases usando os áudios nativos pré-gravados do R2.
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className={`px-3 py-1.5 rounded-full text-xs font-normal uppercase font-mono tracking-[0.14em] ${isPrerecordedEnabled ? 'bg-ok/20 text-ok border border-ok/30' : 'bg-warn/20 text-warn border border-warn/30'}`}>
                    Modo R2 no App: {isPrerecordedEnabled ? 'ATIVADO' : 'DESATIVADO'}
                  </span>
                </div>
              </div>

              {/* Form Controls */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-xs font-normal text-slate-400 uppercase font-mono tracking-[0.14em]">Idioma do Texto</label>
                  <select
                    value={selectedLang}
                    onChange={(e) => setSelectedLang(e.target.value)}
                    className="w-full bg-slate-950 border border-slate-800 rounded-2xl px-4 py-3 font-bold text-white outline-none focus:border-warn transition-colors cursor-pointer"
                  >
                    <option value="pt">🇧🇷 Português (5k palavras)</option>
                    <option value="en">🇺🇸 Inglês (15k palavras)</option>
                    <option value="fr">🇫🇷 Francês (27k palavras)</option>
                    <option value="it">🇮🇹 Italiano (20k palavras)</option>
                  </select>
                </div>
              </div>

              {/* Textarea Input */}
              <div className="space-y-2">
                <label className="text-xs font-normal text-slate-400 uppercase font-mono tracking-[0.14em]">Texto Livre para Teste</label>
                <textarea
                  rows={4}
                  value={ttsInputText}
                  onChange={(e) => setTtsInputText(e.target.value)}
                  placeholder="Digite ou cole o texto aqui para testar a reprodução contínua..."
                  className="w-full bg-slate-950 border border-slate-800 rounded-2xl p-4 font-medium text-slate-200 placeholder:text-slate-600 outline-none focus:border-warn transition-colors resize-y leading-relaxed text-sm"
                />
              </div>

              {/* Action Controls */}
              <div className="flex items-center gap-4">
                {!isPlayingContinuous ? (
                  <button
                    onClick={handleStartContinuousPlay}
                    disabled={!ttsInputText.trim()}
                    className="flex-1 py-4 px-6 bg-warn hover:bg-warn disabled:opacity-50 text-ink rounded-2xl font-medium text-[13px] flex items-center justify-center gap-2 transition-all active:scale-95 cursor-pointer"
                  >
                    <Play size={18} />
                    <span>Iniciar Leitura Contínua (Play)</span>
                  </button>
                ) : (
                  <button
                    onClick={handleStopContinuousPlay}
                    className="flex-1 py-4 px-6 bg-bad hover:bg-bad text-surface rounded-2xl font-medium text-[13px] flex items-center justify-center gap-2 transition-all active:scale-95 cursor-pointer"
                  >
                    <Pause size={18} />
                    <span>Parar Reprodução (Stop)</span>
                  </button>
                )}
              </div>

              {/* Execution Report Card */}
              {executionReport && (
                <div className="bg-slate-950 border border-slate-800 rounded-2xl p-5 space-y-4 font-mono text-xs">
                  <div className="flex items-center justify-between text-slate-400 border-b border-slate-800 pb-3 font-sans font-bold">
                    <span className="text-slate-200">Relatório da Execução</span>
                    <span className="text-ok bg-ok/10 px-2.5 py-1 rounded-md">{executionReport.status}</span>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-center">
                    <div className="bg-slate-900 p-3 rounded-xl border border-slate-800/80">
                      <div className="text-slate-500 text-[10px] uppercase tracking-[0.14em] font-mono font-normal">Total Palavras</div>
                      <div className="text-lg font-semibold text-white">{executionReport.total}</div>
                    </div>
                    <div className="bg-tint-sage/30 p-3 rounded-xl border border-tint-sage/40">
                      <div className="text-ok text-[10px] uppercase tracking-[0.14em] font-mono font-normal">R2 Encontrados</div>
                      <div className="text-lg font-semibold text-ok">{executionReport.r2Count}</div>
                    </div>
                    <div className="bg-tint-butter/30 p-3 rounded-xl border border-tint-butter/40">
                      <div className="text-warn text-[10px] uppercase tracking-[0.14em] font-mono font-normal">TTS Nativo (Fallback)</div>
                      <div className="text-lg font-semibold text-warn">{executionReport.fallbackCount}</div>
                    </div>
                    <div className="bg-tint-rose/30 p-3 rounded-xl border border-tint-rose/40">
                      <div className="text-bad text-[10px] uppercase tracking-[0.14em] font-mono font-normal">Não no R2</div>
                      <div className="text-lg font-semibold text-bad">{executionReport.missingCount}</div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {uploadError && (
            <div className="p-4 bg-tint-rose/60 border border-tint-rose text-bad rounded-2xl text-xs flex items-center gap-3">
              <AlertCircle size={20} className="shrink-0" />
              <span>{uploadError}</span>
            </div>
          )}

          {/* EPUB Files List */}
          {!activeEpubId ? (
            <div className="space-y-4">
              <h2 className="text-sm font-medium text-slate-400 flex items-center gap-2">
                <BookOpen size={16} /> Arquivos EPUB Importados ({savedEpubs.length})
              </h2>

              {savedEpubs.length === 0 ? (
                <div className="p-12 text-center border border-dashed border-slate-800 rounded-3xl bg-slate-900/40 space-y-4">
                  <div className="w-16 h-16 bg-slate-800/80 rounded-2xl mx-auto flex items-center justify-center text-slate-400">
                    <FileText size={32} />
                  </div>
                  <div>
                    <h3 className="font-bold text-white text-base">Nenhum arquivo EPUB importado</h3>
                    <p className="text-xs text-slate-400 mt-1 max-w-md mx-auto">
                      Clique no botão "+" acima para carregar um arquivo .epub com arquivos .smil e narração em áudio MP3 para testar a sincronia.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {savedEpubs.map((epub) => (
                    <div 
                      key={epub.id} 
                      className="p-5 bg-slate-900/90 border border-slate-800 hover:border-warn/50 rounded-2xl flex items-center justify-between gap-4 transition-all group"
                    >
                      <div className="flex items-center gap-4 overflow-hidden">
                        <div className="w-12 h-12 bg-warn/10 text-warn rounded-2xl flex items-center justify-center shrink-0">
                          <BookOpen size={24} />
                        </div>
                        <div className="truncate">
                          <h4 className="font-semibold text-white text-sm truncate">{epub.title}</h4>
                          <p className="text-[11px] text-slate-400 truncate mt-0.5">{epub.fileName}</p>
                          <span className="text-[10px] text-slate-500 font-mono">
                            {(epub.size / (1024 * 1024)).toFixed(2)} MB • {new Date(epub.addedAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2 shrink-0">
                        <button
                          onClick={() => openEpubReader(epub.id)}
                          className="px-4 py-2 bg-slate-800 hover:bg-warn hover:text-slate-950 font-medium text-[13px] rounded-xl transition-all flex items-center gap-1.5"
                        >
                          <Eye size={14} /> Abrir Leitor
                        </button>
                        <button
                          onClick={(e) => handleDeleteEpub(epub.id, e)}
                          className="p-2 hover:bg-tint-rose hover:text-bad text-slate-500 rounded-xl transition-colors"
                          title="Excluir arquivo"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            /* EPUB Reader & Sync Test View */
            <div className="space-y-6">
              {isLoadingReader ? (
                <div className="p-12 text-center bg-slate-900 rounded-3xl space-y-4">
                  <Loader2 className="w-10 h-10 text-warn animate-spin mx-auto" />
                  <p className="text-xs font-normal uppercase tracking-[0.14em] font-mono text-slate-400">Processando pacote EPUB e arquivos SMIL...</p>
                </div>
              ) : parsedEpub ? (
                <div className="bg-slate-900 border border-slate-800 rounded-3xl p-6 space-y-6">
                  {/* Reader Header / Controls */}
                  <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-800 pb-4">
                    <div>
                      <h2 className="text-lg font-semibold text-white">{parsedEpub.title}</h2>
                      <p className="text-xs text-warn font-medium mt-0.5">
                        {parsedEpub.chapters.length} capítulo(s) • {parsedEpub.audioBlobs.size} áudio(s) mapeado(s)
                      </p>
                    </div>

                    <div className="flex items-center gap-4 flex-wrap">
                      {/* Chapter Selector */}
                      <select
                        value={currentChapterIdx}
                        onChange={(e) => {
                          setCurrentChapterIdx(Number(e.target.value));
                          setCurrentClip(null);
                          setIsPlaying(false);
                          if (audioRef.current) audioRef.current.pause();
                        }}
                        className="bg-slate-800 border border-slate-700 text-slate-200 text-xs font-bold rounded-xl px-3 py-2 outline-none focus:border-warn"
                      >
                        {parsedEpub.chapters.map((ch, idx) => (
                          <option key={ch.id} value={idx}>
                            {ch.title} ({ch.smilClips.length} marcações SMIL)
                          </option>
                        ))}
                      </select>

                      {/* Playback Button */}
                      <button
                        onClick={togglePlayPause}
                        className="px-5 py-2.5 bg-warn hover:bg-warn text-ink font-medium text-[13px] rounded-xl flex items-center gap-2 transition-all"
                      >
                        {isPlaying ? <Pause size={16} /> : <Play size={16} />}
                        {isPlaying ? 'Pausar' : 'Reproduzir SMIL'}
                      </button>

                      {/* Highlight Narration Toggle */}
                      <label className="flex items-center gap-2 cursor-pointer bg-slate-800 px-3.5 py-2 rounded-xl border border-slate-700 hover:border-slate-600">
                        <input
                          type="checkbox"
                          checked={highlightEnabled}
                          onChange={(e) => setHighlightEnabled(e.target.checked)}
                          className="accent-warn w-4 h-4 rounded cursor-pointer"
                        />
                        <span className="text-xs font-bold text-slate-200">Destaque de Narração</span>
                      </label>
                    </div>
                  </div>

                  {/* Status / Debug Banner */}
                  {audioError ? (
                    <div className="p-4 bg-tint-butter/40 border border-tint-butter/60 rounded-2xl text-xs text-warn flex items-center gap-3">
                      <AlertCircle size={20} className="shrink-0 text-warn" />
                      <div>
                        <strong className="block font-bold">Status de Depuração de Sincronia:</strong>
                        <span>{audioError}</span>
                      </div>
                    </div>
                  ) : currentClip ? (
                    <div className="p-3 bg-slate-800/60 border border-slate-700/60 rounded-2xl text-xs flex items-center justify-between">
                      <div className="flex items-center gap-2 text-slate-300">
                        <Music size={16} className="text-warn animate-pulse" />
                        <span>Segmento Ativo: <strong>{currentClip.elementId}</strong></span>
                        <span className="text-slate-500">|</span>
                        <span className="font-mono text-slate-400">
                          Offset: {currentClip.clipBegin.toFixed(2)}s - {currentClip.clipEnd.toFixed(2)}s
                        </span>
                      </div>
                      <span className="text-[10px] font-medium text-warn bg-warn/10 px-2 py-1 rounded-md">
                        Sincronizado
                      </span>
                    </div>
                  ) : (
                    <div className="p-3 bg-slate-800/30 rounded-2xl text-xs text-slate-400 flex items-center gap-2">
                      <BookOpen size={16} />
                      <span>Clique em qualquer parágrafo/palavra no texto para iniciar a narração a partir daquele ponto (Click-to-play).</span>
                    </div>
                  )}

                  {/* Rendered Chapter Document */}
                  {currentChapter ? (
                    <div
                      ref={readerContainerRef}
                      onClick={handleReaderClick}
                      className="prose prose-invert max-w-none p-6 md:p-8 bg-slate-950 rounded-2xl border border-slate-800 text-slate-200 leading-relaxed cursor-pointer selection:bg-warn selection:text-slate-950"
                      dangerouslySetInnerHTML={{ __html: sanitizeHtml(currentChapter.htmlContent) }}
                    />
                  ) : (
                    <div className="p-8 text-center text-slate-500 text-xs">Capítulo não carregado.</div>
                  )}
                </div>
              ) : null}
            </div>
          )}
        </>
      )}

    </div>
  );
}
